/**
 * useAnythingLLM — Hook for communicating with the support-chat Edge Function proxy
 *
 * Provides a clean interface for sending chat messages, managing conversation state,
 * page-context awareness, multi-turn expert sessions, equipment scope, flow suggestions,
 * and submitting user feedback (thumbs up/down).
 */

import { useState, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useHelpPageContext } from '../../help-chat/HelpPageContext';
import { useOptionalSupportHub } from '../SupportHubContext';
import { track } from '../../../lib/analytics';
import type {
    HelpChatResponse,
    HelpChatHistoryItem,
    ChatMessage,
    ChatSource,
    EquipmentScope,
    FlowSuggestion,
} from '../../help-chat/helpChat.types';

export type { ChatMessage, ChatSource };

let messageCounter = 0;
function generateId(): string {
    return `msg-${Date.now()}-${++messageCounter}`;
}

export function stripTrailingSourcesLine(text: string): string {
    if (!text) return '';
    return text.replace(/\n\s*Sources:[^\n]*\s*$/i, '').trim();
}

export function stripCitationTags(text: string): string {
    if (!text) return '';
    return text
        .replace(/\[\^?(?:A|S)?\d+\]/g, '')
        .replace(/ +([.,;!?])/g, '$1')
        .replace(/ {2,}/g, ' ')
        .trim();
}

export function cleanAssistantResponseText(text: string): string {
    return stripCitationTags(stripTrailingSourcesLine(text));
}

export function useAnythingLLM() {
    const hub = useOptionalSupportHub();

    const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
    const [localIsLoading, setLocalIsLoading] = useState(false);
    const [localExpertSessionId, setLocalExpertSessionId] = useState<string | null>(null);
    const [localEquipmentScope, setLocalEquipmentScope] = useState<EquipmentScope | null>(null);
    const [localFlowOffer, setLocalFlowOffer] = useState<FlowSuggestion | null>(null);
    const [localFlowOffers, setLocalFlowOffers] = useState<FlowSuggestion[]>([]);
    const [localDismissedFlowIds, setLocalDismissedFlowIds] = useState<Set<string>>(() => new Set());
    const localAbortControllerRef = useRef<AbortController | null>(null);

    const messages = hub?.messages ?? localMessages;
    const setMessages = hub?.setMessages ?? setLocalMessages;
    const isLoading = hub?.isLoading ?? localIsLoading;
    const setIsLoading = hub?.setIsLoading ?? setLocalIsLoading;
    const abortControllerRef = hub?.chatAbortControllerRef ?? localAbortControllerRef;

    const expertSessionId = hub?.expertSessionId !== undefined ? hub.expertSessionId : localExpertSessionId;
    const setExpertSessionId = hub?.setExpertSessionId ?? setLocalExpertSessionId;

    const equipmentScope = hub?.equipmentScope !== undefined ? hub.equipmentScope : localEquipmentScope;
    const setEquipmentScope = hub?.setEquipmentScope ?? setLocalEquipmentScope;

    const flowOffers = hub?.flowOffers ?? localFlowOffers;
    const setFlowOffers = hub?.setFlowOffers ?? setLocalFlowOffers;

    const dismissedFlowIds = hub?.dismissedFlowIds ?? localDismissedFlowIds;
    const setDismissedFlowIds = hub?.setDismissedFlowIds ?? setLocalDismissedFlowIds;

    const flowOffer =
        hub?.flowOffer !== undefined
            ? hub.flowOffer
            : (flowOffers.find((o) => !dismissedFlowIds.has(o.flow_id)) ?? localFlowOffer);
    const setFlowOffer = hub?.setFlowOffer ?? setLocalFlowOffer;

    const { currentPageId } = useHelpPageContext();

    const sendMessage = useCallback(
        async (content: string, contextIdOverride?: string | null) => {
            if (!content.trim() || isLoading) return;

            const trimmedContent = content.trim();

            // Add user message
            const userMsg: ChatMessage = {
                id: generateId(),
                role: 'user',
                content: trimmedContent,
                timestamp: new Date(),
            };

            // Add loading placeholder for assistant
            const loadingMsg: ChatMessage = {
                id: generateId(),
                role: 'assistant',
                content: '',
                timestamp: new Date(),
                isLoading: true,
                userQuestion: trimmedContent,
            };

            setMessages((prev) => [...prev, userMsg, loadingMsg]);
            setIsLoading(true);

            // Abort any in-flight request
            abortControllerRef.current?.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;

            // Send up to 6 valid previous turns as history (excluding loading and errors)
            const history: HelpChatHistoryItem[] = messages
                .filter((m) => !m.isLoading && !m.error && m.content.trim().length > 0)
                .slice(-6)
                .map((m) => ({
                    role: m.role,
                    content: m.content.slice(0, 1500),
                }));

            const effectiveContextId =
                contextIdOverride !== undefined ? contextIdOverride : (currentPageId ?? null);

            try {
                const requestPayload: Record<string, unknown> = {
                    message: trimmedContent,
                    history,
                    context_id: effectiveContextId,
                    platform: 'web',
                    client_capabilities: ['safety_banner', 'expert_session'],
                };

                if (expertSessionId) {
                    requestPayload.expert_session_id = expertSessionId;
                }

                const { data, error } = await supabase.functions.invoke('support-chat', {
                    signal: controller.signal,
                    body: requestPayload,
                });

                if (controller.signal.aborted) return;

                if (error) {
                    throw new Error(error.message || 'Failed to get response');
                }

                const responseData = data as HelpChatResponse;

                if (responseData.error) {
                    throw new Error(responseData.error);
                }

                // Update expert session id if returned
                const sessionChanged = Boolean(
                    responseData.expert_session_id &&
                        responseData.expert_session_id !== expertSessionId,
                );
                if (responseData.expert_session_id) {
                    setExpertSessionId(responseData.expert_session_id);
                }
                if (sessionChanged) {
                    setDismissedFlowIds(new Set());
                }

                // Update equipment scope if returned
                let mfrChanged = false;
                if (responseData.equipment_scope !== undefined) {
                    const nextScope = responseData.equipment_scope ?? null;
                    const prevMfrId = equipmentScope?.manufacturer_id ?? null;
                    const nextMfrId = nextScope?.manufacturer_id ?? null;
                    mfrChanged = nextMfrId !== prevMfrId;
                    setEquipmentScope(nextScope);
                }

                // Merge flow suggestions / offer: new suggestions merge, never replace
                const incoming: FlowSuggestion[] = [];
                if (responseData.flow_offer) {
                    incoming.push({
                        flow_id: responseData.flow_offer.flow_id,
                        title: responseData.flow_offer.title,
                        match: responseData.flow_offer.match,
                        error_codes: responseData.flow_offer.error_codes,
                    });
                }
                if (Array.isArray(responseData.flow_suggestions)) {
                    for (const s of responseData.flow_suggestions) {
                        if (!incoming.some((i) => i.flow_id === s.flow_id)) {
                            incoming.push(s);
                        }
                    }
                }

                const effectiveDismissed = sessionChanged ? new Set<string>() : dismissedFlowIds;

                if (incoming.length > 0) {
                    setFlowOffers((prev) => {
                        const basePrev = mfrChanged || sessionChanged ? [] : prev;
                        const merged: FlowSuggestion[] = [];
                        // 1. flow_offer first, then incoming suggestions (if undismissed)
                        for (const item of incoming) {
                            if (!effectiveDismissed.has(item.flow_id) && !merged.some((m) => m.flow_id === item.flow_id)) {
                                merged.push(item);
                            }
                        }
                        // 2. then undismissed previous offers not in incoming
                        for (const prevItem of basePrev) {
                            if (!effectiveDismissed.has(prevItem.flow_id) && !merged.some((m) => m.flow_id === prevItem.flow_id)) {
                                merged.push(prevItem);
                            }
                        }
                        return merged;
                    });
                } else if (mfrChanged) {
                    setFlowOffers([]);
                }

                // Clean text response: strip trailing Sources: line and raw citation tags
                const cleanedContent = cleanAssistantResponseText(responseData.textResponse || '');

                const assistantMsg: ChatMessage = {
                    id: loadingMsg.id,
                    role: 'assistant',
                    content:
                        cleanedContent ||
                        "I couldn't find an answer to that. Try rephrasing your question or browse the Help Center.",
                    sources: responseData.sources ?? [],
                    timestamp: new Date(),
                    version: responseData.version,
                    message_id: responseData.message_id ?? null,
                    outcome: responseData.outcome,
                    assistant_mode:
                        responseData.assistant_mode ??
                        (responseData.outcome === 'PRODUCT' ? 'PRODUCT' : 'PLATFORM'),
                    expert_session_id: responseData.expert_session_id ?? expertSessionId ?? null,
                    equipment_scope: responseData.equipment_scope,
                    flow_suggestions: responseData.flow_suggestions,
                    flow_offer: responseData.flow_offer,
                    answer_basis: responseData.answer_basis,
                    answer_status: responseData.answer_status,
                    risk_class: responseData.risk_class,
                    safety_level: responseData.safety_level,
                    conflicts: responseData.conflicts,
                    suggest_escalation: responseData.suggest_escalation,
                    actions: responseData.actions ?? [],
                    feedbackRating: null,
                };

                setMessages((prev) =>
                    prev.map((m) => (m.id === loadingMsg.id ? assistantMsg : m)),
                );
                track('assistant_question_asked', { surface: 'help' });
            } catch (err: unknown) {
                if (err instanceof Error && err.name === 'AbortError') return;

                const errorMsg: ChatMessage = {
                    id: loadingMsg.id,
                    role: 'assistant',
                    content: '',
                    timestamp: new Date(),
                    error:
                        err instanceof Error
                            ? err.message
                            : 'Something went wrong. Please try again.',
                };

                setMessages((prev) =>
                    prev.map((m) => (m.id === loadingMsg.id ? errorMsg : m)),
                );
            } finally {
                setIsLoading(false);
            }
        },
        [
            isLoading,
            messages,
            currentPageId,
            expertSessionId,
            equipmentScope?.manufacturer_id,
            dismissedFlowIds,
            setMessages,
            setIsLoading,
            abortControllerRef,
            setExpertSessionId,
            setEquipmentScope,
            setFlowOffers,
            setDismissedFlowIds,
        ],
    );

    const sendFeedback = useCallback(
        async (messageId: string, rating: 1 | -1) => {
            let prevRating: 1 | -1 | null = null;

            // Optimistically update rating and mark feedback as in-flight
            setMessages((prev) =>
                prev.map((m) => {
                    if (m.message_id === messageId || m.id === messageId) {
                        prevRating = m.feedbackRating ?? null;
                        return { ...m, feedbackRating: rating, isFeedbackSubmitting: true };
                    }
                    return m;
                }),
            );

            try {
                const { data, error } = await supabase.functions.invoke('support-chat', {
                    body: {
                        action: 'feedback',
                        message_id: messageId,
                        rating,
                    },
                });

                if (error || (data && data.ok === false)) {
                    throw new Error(error?.message || data?.error || 'Failed to submit feedback');
                }

                track('assistant_feedback', { surface: 'help', helpful: rating === 1 });

                setMessages((prev) =>
                    prev.map((m) => {
                        if (m.message_id === messageId || m.id === messageId) {
                            return { ...m, isFeedbackSubmitting: false };
                        }
                        return m;
                    }),
                );
            } catch (err) {
                // Revert rating and clear in-flight flag on error
                setMessages((prev) =>
                    prev.map((m) => {
                        if (m.message_id === messageId || m.id === messageId) {
                            return { ...m, feedbackRating: prevRating, isFeedbackSubmitting: false };
                        }
                        return m;
                    }),
                );
                throw err;
            }
        },
        [setMessages],
    );

    const clearMessages = useCallback(() => {
        abortControllerRef.current?.abort();
        setMessages([]);
        setIsLoading(false);
        setExpertSessionId(null);
        setEquipmentScope(null);
        setFlowOffer(null);
        setFlowOffers([]);
        setDismissedFlowIds(new Set());
    }, [
        abortControllerRef,
        setIsLoading,
        setMessages,
        setExpertSessionId,
        setEquipmentScope,
        setFlowOffer,
        setFlowOffers,
        setDismissedFlowIds,
    ]);

    const dismissFlow = useCallback(
        (flowId?: string) => {
            if (flowId) {
                setDismissedFlowIds((prev) => {
                    const next = new Set(prev);
                    next.add(flowId);
                    return next;
                });
                setFlowOffers((prev) => prev.filter((o) => o.flow_id !== flowId));
            } else {
                const targetId = flowOffer?.flow_id ?? flowOffers[0]?.flow_id;
                if (targetId) {
                    setDismissedFlowIds((prev) => {
                        const next = new Set(prev);
                        next.add(targetId);
                        return next;
                    });
                    setFlowOffers((prev) => prev.filter((o) => o.flow_id !== targetId));
                }
            }
        },
        [flowOffer, flowOffers, setDismissedFlowIds, setFlowOffers],
    );

    const consumeFlow = useCallback(
        (flowId: string) => {
            setDismissedFlowIds((prev) => {
                const next = new Set(prev);
                next.add(flowId);
                return next;
            });
            setFlowOffers((prev) => prev.filter((o) => o.flow_id !== flowId));
            hub?.consumeFlowOffer?.(flowId);
        },
        [hub, setDismissedFlowIds, setFlowOffers],
    );

    return {
        messages,
        isLoading,
        expertSessionId,
        equipmentScope,
        setEquipmentScope,
        flowOffer,
        flowOffers,
        setFlowOffers,
        dismissFlow,
        consumeFlow,
        sendMessage,
        clearMessages,
        sendFeedback,
    };
}

export const useHelpChat = useAnythingLLM;
